/**
 * @file Per-file temp-folder layout used by the AI harness. Every uploaded
 * file gets its own folder at `temp/<userId>/<fileFolderId>/`; the original
 * blob and every processed variant live inside that folder under fixed stage
 * names. The `parsed` stage holds up to three fixed files: `labeled.json` (the
 * cooked `Chunk[]`, always present), `rawParseResult.json` (the verbatim
 * provider response, present only when a document-AI parser ran), and
 * `encoding.json` (the `TextEncodingFacts`, present only when a text/CSV load
 * detected an encoding). The `evidence` stage holds the PDF evidence set:
 * `pages/<n>.json` (one `EvidencePage`), `pageSortRenders/<n>.png`,
 * `renders/<n>.png`, `crops/<n>-<tableId>.png` and `calls/<callId>.{input,response}.json` (the
 * exact input and verbatim response behind one `ExtractionCall`). The display
 * filename and upload date are stored as `customMetadata` on the original blob
 * — never derived from the path. This module owns the stage union, the path
 * builder, and the tenant-isolation guard so no other module spells out a
 * stage name. It also owns the Doc AI async-batch staging layout
 * (`<baseDir>/docaiBatch/…`): unlike the stage paths, `baseDir` is not always a
 * tenant-guarded temp folder — it is an agent `fileFolder` OR an email
 * attachment storage path, so the `docAiBatch*` builders carry no
 * `assertFileFolder`-style ownership guarantee.
 */

/** A typed stage name within a per-file temp folder. */
export type FileStage =
  | 'original'
  | 'parsed'
  | 'parsedEmail'
  | 'process'
  | 'partResolutions'
  | 'evidence';

/**
 * The fixed files under the `parsed/` stage directory. `labeled` always exists;
 * `rawParseResult` only after a document-AI parse; `encoding` only when a
 * text/CSV load detected an encoding (an optional third file).
 */
export type ParsedFile = 'labeled' | 'rawParseResult' | 'encoding';

/**
 * The files under the `evidence/` stage directory, by kind. `page` is 1-based.
 * `call` names one half of a model call's record: the exact input sent or the
 * verbatim response.
 */
export type EvidenceFile =
  | { kind: 'page'; page: number }
  | { kind: 'pageSortRender'; page: number }
  | { kind: 'render'; page: number }
  | { kind: 'crop'; page: number; tableId: string }
  | { kind: 'call'; callId: string; part: 'input' | 'response' };

/** Args for stages that take a parameter. */
export type StageArgs = {
  original: { extension: string };
  parsed: { name: ParsedFile };
  parsedEmail: Record<string, never>;
  process: { index: number };
  partResolutions: Record<string, never>;
  evidence: EvidenceFile;
};

const FILE_FOLDER_RE = /^temp\/([A-Za-z0-9_-]+)\/([A-Za-z0-9_-]+)$/;

/** Builds the GCS object path for a stage inside `fileFolder`. */
export function pathForStage<S extends FileStage>(
  fileFolder: string,
  stage: S,
  args: StageArgs[S],
): string {
  switch (stage) {
    case 'original': {
      const { extension } = args as StageArgs['original'];
      return `${fileFolder}/original.${extension}`;
    }
    case 'parsed': {
      const { name } = args as StageArgs['parsed'];
      return `${fileFolder}/parsed/${name}.json`;
    }
    case 'parsedEmail':
      return `${fileFolder}/parsedEmail.json`;
    case 'process': {
      const { index } = args as StageArgs['process'];
      return `${fileFolder}/process/${index}.json`;
    }
    case 'partResolutions':
      return `${fileFolder}/partResolutions.json`;
    case 'evidence':
      return evidencePath(fileFolder, args as StageArgs['evidence']);
  }
}

function evidencePath(fileFolder: string, file: EvidenceFile): string {
  switch (file.kind) {
    case 'page':
      return `${fileFolder}/evidence/pages/${file.page}.json`;
    case 'pageSortRender':
      return `${fileFolder}/evidence/pageSortRenders/${file.page}.png`;
    case 'render':
      return `${fileFolder}/evidence/renders/${file.page}.png`;
    case 'crop':
      return `${fileFolder}/evidence/crops/${file.page}-${file.tableId}.png`;
    case 'call':
      return `${fileFolder}/evidence/calls/${file.callId}.${file.part}.json`;
  }
}

/** Returns the directory prefix (with trailing slash) where indexed stages live. */
export function stageDirectory(fileFolder: string, stage: 'parsed' | 'process' | 'evidence'): string {
  return `${fileFolder}/${stage}/`;
}

/** Returns the prefix used to locate `original.<ext>` without knowing the extension. */
export function originalPrefix(fileFolder: string): string {
  return `${fileFolder}/original.`;
}

/**
 * How long a `parsing.lock` holder may live before its lock is reclaimed as
 * crashed: the 300 s deadline of every function that takes the lock. Also how
 * long a caller answered `processing` because the lock is held must keep
 * polling before giving up.
 */
export const PARSING_LOCK_STALE_MS = 300_000;

/**
 * Root-level lock object guarding a single synchronous, paid document parse of a
 * file. Created atomically (`ifGenerationMatch: 0`) before the sync Document AI
 * call and deleted when the parse settles, so two concurrent `httpReadFileData`
 * requests for the same not-yet-parsed file cannot both issue a paid call.
 * `httpBuildPdfEvidence` holds the same lock while it builds pages, whose OCR
 * fallback is paid too, so the two never bill one file at once. Lives
 * at the fileFolder root — deliberately NOT under `parsed/` — so the email
 * provenance copy (which enumerates `parsed/`) never mistakes it for an
 * extracted artifact.
 */
export function parsingLockPath(fileFolder: string): string {
  return `${fileFolder}/parsing.lock`;
}

/**
 * Path segment identifying a GCP Document AI batch output object. The completion
 * trigger filters finalized objects by this segment. Owned here so no other
 * module spells out the batch staging layout.
 */
export const DOCAI_BATCH_OUTPUT_SEGMENT = 'docaiBatch/out/';

/** GCS prefix (bucket-relative) Doc AI writes batch output shards to, under a base dir. */
export function docAiBatchOutputPrefix(baseDir: string): string {
  return `${baseDir}/${DOCAI_BATCH_OUTPUT_SEGMENT}`;
}

/** The whole batch staging dir (marker + output shards + error record) under a base dir. */
export function docAiBatchStagingPrefix(baseDir: string): string {
  return `${baseDir}/docaiBatch/`;
}

/** The batch operation marker path (bucket-relative), a sibling of the output dir. */
export function docAiBatchMarkerPath(baseDir: string): string {
  return `${baseDir}/docaiBatch/operation.json`;
}

/** The terminal-failure record a completed-with-error batch leaves behind. */
export function docAiBatchErrorPath(baseDir: string): string {
  return `${baseDir}/docaiBatch/error.json`;
}

/** From a finalized batch-output object name, the base dir (parent of `docaiBatch/out/…`), or null. */
export function docAiBatchBaseDirFromOutput(objectName: string): string | null {
  const marker = `/${DOCAI_BATCH_OUTPUT_SEGMENT}`;
  const idx = objectName.indexOf(marker);
  return idx <= 0 ? null : objectName.slice(0, idx);
}

/**
 * Validates `fileFolder` shape and ownership. Throws if `fileFolder` is not
 * `temp/<actorUid>/<id>`.
 */
export function assertFileFolder(fileFolder: unknown, actorUid: string, label = 'fileFolder'): string {
  if (typeof fileFolder !== 'string') {
    throw new Error(`Invalid ${label}: must be a string of shape temp/<userId>/<fileId>`);
  }
  const match = FILE_FOLDER_RE.exec(fileFolder);
  if (!match) {
    throw new Error(`Invalid ${label}: must match temp/<userId>/<fileId>`);
  }
  if (match[1] !== actorUid) {
    throw new Error(`${label} does not belong to the calling user`);
  }
  return fileFolder;
}

/**
 * Expands a model-supplied `fileId` (the durable per-file handle surfaced to the
 * agent as `<file_id>`) into the validated per-file folder for `actorUid`. The
 * single boundary where the agent-facing handle becomes a Storage path. Because
 * `actorUid` is injected server-side, the agent cannot address another tenant's
 * folder, and the ownership branch of `assertFileFolder` is unreachable from
 * here. What this boundary actually enforces is the SHAPE of `fileId`: empty,
 * slash-bearing, or traversal-character handles fail the shape check.
 */
export function buildFileFolder(actorUid: string, fileId: string): string {
  return assertFileFolder(`temp/${actorUid}/${fileId}`, actorUid, 'fileId');
}

/** Extracts the lowercased extension (no leading dot) from a filename. Throws if none. */
export function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf('.');
  if (dot < 0 || dot === filename.length - 1) {
    throw new Error(`Filename has no extension: ${filename}`);
  }
  return filename.slice(dot + 1).toLowerCase();
}

/** Custom metadata stored on the `original.<ext>` blob. */
export interface OriginalBlobMetadata {
  displayName: string;
  uploadedAt: string;
}
